/**
 * GlobalToast: 우하단 채팅형 토스트 컴포넌트
 *
 * 마크업 기준: toast.html의 .rtcNewPopover.typeMessage
 * - 오른쪽에서 슬라이드 인 (fadeRightLeft_ani)
 * - duration 후 페이드 아웃 (fadeOut_ani)
 * - 페이드 아웃 완료 후 DOM 제거
 *
 * 전역 함수 showToast()로 호출.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import SNSnackbar from '@luna-sena/sena-snackbar';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { dismissToast } from '@/utils/globalToast';
import { toastActions } from '@/store/slices/toastSlice';
import type { GlobalToastMessage, SnackbarQueueItem } from '@/store/slices/toastSlice';
import { translateText } from '@/i18n';

// ─── 개별 토스트 아이템 ───────────────────────────────────────────────────

const ToastItem: React.FC<{ toast: GlobalToastMessage }> = ({ toast }) => {
  const [animClass, setAnimClass] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const duration = toast.duration ?? 4000;
  // 페이드아웃 애니메이션 시간 (CSS @keyframes rtcNewPopover_fadeout: 3s)
  const fadeOutDuration = 3000;

  useEffect(() => {
    // 1) 마운트 직후 슬라이드 인
    const slideInTimer = setTimeout(() => {
      setAnimClass('fadeRightLeft_ani');
    }, 50);

    // 2) duration 후 페이드 아웃 시작
    const fadeOutTimer = setTimeout(() => {
      setAnimClass('fadeRightLeft_ani fadeOut_ani');
    }, 50 + duration);

    // 3) 페이드 아웃 완료 후 제거
    const removeTimer = setTimeout(() => {
      dismissToast(toast.id);
    }, 50 + duration + fadeOutDuration);

    timerRef.current = removeTimer;

    return () => {
      clearTimeout(slideInTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(removeTimer);
    };
  }, [toast.id, duration]);

  return (
    <div className={`rtcNewPopover__message ${animClass}`} data-no-i18n>
      <strong className="rtcNewPopover__messageTitle ellipsis">{toast.title}</strong>
      <p className="rtcNewPopover__messageText ellipsis">{toast.message}</p>
    </div>
  );
};

// ─── SNSnackbar 아이콘 SVG (SNA 컴포넌트 내부와 동일) ─────────────────────

const snackbarIconPath = {
  success: (
    <path d="M20.949 7.061a1.332 1.332 0 0 0-1.885-.013l-8.917 8.794-5.241-4.855a1.333 1.333 0 1 0-1.812 1.957l6.176 5.72a1.331 1.331 0 0 0 1.842-.029l9.824-9.689c.525-.517.53-1.361.013-1.885Z" />
  ),
  info: (
    <path d="M11.997 8c-.736 0-1.333.597-1.333 1.333V20a1.333 1.333 0 0 0 2.666 0V9.333c0-.736-.597-1.333-1.333-1.333Zm0-2.667a1.333 1.333 0 1 0 0-2.667 1.333 1.333 0 0 0 0 2.667Z" />
  ),
  error: (
    <path d="M11.997 18.667a1.332 1.332 0 1 0 0 2.666 1.332 1.332 0 1 0 0-2.666Zm0-16c-.737 0-1.333.596-1.333 1.333v10.667a1.332 1.332 0 1 0 2.666 0V4c0-.737-.596-1.333-1.333-1.333Z" />
  ),
};

// ─── 스택형 스낵바 아이템 ─────────────────────────────────────────────────

const StackedSnackbarItem: React.FC<{
  item: SnackbarQueueItem;
  onRemove: (id: number) => void;
}> = ({ item, onRemove }) => {
  const [phase, setPhase] = useState<'enter' | 'visible' | 'exit' | 'done'>('enter');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // fadein 후 visible 전환
    const enterTimer = setTimeout(() => setPhase('visible'), 500);
    // autoHide: 3초 후 exit 시작
    const exitTimer = setTimeout(() => setPhase('exit'), 3000);
    // exit 애니메이션(0.5s) 후 제거
    const removeTimer = setTimeout(() => {
      setPhase('done');
      onRemove(item.id);
    }, 3500);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);
    };
  }, [item.id, onRemove]);

  if (phase === 'done') return null;

  const animation =
    phase === 'enter'
      ? 'SNA_toast_fadein .5s'
      : phase === 'exit'
        ? 'SNA_toast_fadeout .5s forwards'
        : 'none';

  return (
    <div
      ref={ref}
      className="SNA_snackbar"
      style={{
        display: 'inline-block',
        animation,
        position: 'relative',
        top: 'auto',
        bottom: 'auto',
        left: 'auto',
        right: 'auto',
        transform: 'none',
      }}
    >
      <div className="SNA_snackbar_skin">
        <div className={`SNA_snackbar_iconBox--${item.type}`}>
          <span className="SNA_snackbar_icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="SNA_img">
              {snackbarIconPath[item.type]}
            </svg>
          </span>
        </div>
        <p className="SNA_snackbar_msg">{translateText(item.message)}</p>
      </div>
    </div>
  );
};

// ─── 스택형 스낵바 컨테이너 ───────────────────────────────────────────────

const SnackbarStack: React.FC = () => {
  const dispatch = useAppDispatch();
  const queue = useAppSelector((s) => s.toast.snackbarQueue);

  const handleRemove = useCallback(
    (id: number) => {
      dispatch(toastActions.removeSnackbar(id));
    },
    [dispatch],
  );

  if (queue.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        left: 0,
        right: 0,
        zIndex: 1503,
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      {queue.map((item) => (
        <StackedSnackbarItem key={item.id} item={item} onRemove={handleRemove} />
      ))}
    </div>,
    document.body,
  );
};

// ─── 토스트 컨테이너 ──────────────────────────────────────────────────────

const GlobalToast: React.FC = () => {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector((s) => s.toast.toasts);
  const snackbar = useAppSelector((s) => s.toast.snackbar);

  // SNSnackbar의 autoHide를 사용하지 않고 직접 타이머를 관리한다.
  // SNSnackbar는 open이 true인 채로 메시지만 바뀌면 내부 타이머를 리셋하지 않으므로,
  // seq 변경 시 타이머를 직접 clear → 재시작하여 항상 3초 전체가 보장되도록 한다.
  const snackbarTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(snackbarTimerRef.current);
    if (snackbar.open) {
      const seqAtSetup = snackbar.seq;
      snackbarTimerRef.current = setTimeout(() => {
        // seq를 전달하여 이 타이머가 설정된 스낵바만 닫도록 한다.
        // 타이머 실행 시점에 이미 새 스낵바(seq+1)가 열렸다면 무시된다.
        dispatch(toastActions.closeSnackbar(seqAtSetup));
      }, 3000);
    }
    return () => clearTimeout(snackbarTimerRef.current);
  }, [snackbar.open, snackbar.seq, dispatch]);

  return (
    <>
      {toasts.length > 0 && (
        <div className="rtcNewPopover typeMessage">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} />
          ))}
        </div>
      )}

      <SNSnackbar
        key={snackbar.seq}
        open={snackbar.open}
        type={snackbar.type}
        message={translateText(snackbar.message)}
        autoHideDuration={0}
        onRequestClose={(reason?: string) => {
          if (reason === 'clickaway') return;
          dispatch(toastActions.closeSnackbar());
        }}
      />

      <SnackbarStack />
    </>
  );
};

export default GlobalToast;
